import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, X, Users, Inbox, BookOpen, ClipboardList, ShieldCheck, Lock, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const CAN = [
  ["Add & manage employees in your region", "/admin/users", Users, "users"],
  ["Approve access requests", "/admin/requests", Inbox, "requests"],
  ["Create region-only SOPs, courses, exams & forms", "/sops/new", BookOpen, "content"],
  ["Assign training in your region", "/assignments", ClipboardList, "assign"],
  ["View compliance & form records for your region", "/compliance", ShieldCheck, "compliance"],
];
const CANT = ["Company-wide content", "Regions & communities", "Admin accounts", "Company settings"];

const Chip = ({ label, value, testId }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs" data-testid={testId}>
    <b className="font-display text-base text-lasso-sky" data-testid={`${testId}-value`}>{value}</b>{label}
  </span>
);

export function RegionAdminWelcome() {
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const reopen = sp.get("guide") === "region-admin";

  useEffect(() => {
    (reopen ? api.put("/me/region-admin-welcome", { dismissed: false }).then(() => api.get("/me/region-admin-welcome")) : api.get("/me/region-admin-welcome"))
      .then((r) => setD(r.data)).catch(() => setD(null));
    if (reopen) { const n = new URLSearchParams(sp); n.delete("guide"); setSp(n, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopen]);

  if (!d || d.dismissed) return null;
  const dismiss = () => api.put("/me/region-admin-welcome", { dismissed: true }).then(() => setD({ ...d, dismissed: true }));
  return (
    <section className="fade-up relative mb-10 overflow-hidden rounded-3xl bg-lasso-ink p-6 text-white sm:p-8" data-testid="region-admin-welcome">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-lasso-sky" />
      <button onClick={dismiss} className="absolute right-4 top-4 rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-white" aria-label="Dismiss" data-testid="region-admin-welcome-close"><X className="h-4 w-4" /></button>
      <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-lasso-sky"><MapPin className="h-3.5 w-3.5" /> Lasso Hub · Region Admin</p>
      <h2 className="mt-3 pr-8 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="region-admin-welcome-title">Welcome, Region Admin — {d.region_name}</h2>
      <p className="mt-2 text-sm text-white/70">You can manage people, training and compliance for the {d.region_name} region.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Chip label="communities" value={d.communities} testId="ra-stat-communities" />
        <Chip label="active employees" value={d.active_employees} testId="ra-stat-employees" />
        <Chip label="pending access requests" value={d.pending_requests} testId="ra-stat-requests" />
      </div>
      <div className="mt-7 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <ul className="space-y-1">
          {CAN.map(([label, to, Icon, key]) => (
            <li key={key}>
              <Link to={to} className="group flex items-center gap-3 rounded-xl px-2 py-2 text-sm hover:bg-white/5" data-testid={`ra-can-${key}`}>
                <CheckCircle2 className="h-4 w-4 shrink-0 text-lasso-sky" /><span className="flex-1">{label}</span><Icon className="h-4 w-4 text-white/40 group-hover:text-white" />
              </Link>
            </li>
          ))}
        </ul>
        <div className="rounded-2xl border border-white/10 p-4" data-testid="ra-cant">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">Handled by company admins</p>
          <ul className="mt-3 space-y-2 text-sm text-white/70">{CANT.map((c) => <li key={c} className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-white/40" />{c}</li>)}</ul>
        </div>
      </div>
      <button onClick={dismiss} className="mt-7 rounded-full bg-lasso-sky px-5 py-2.5 text-sm font-semibold text-lasso-ink hover:bg-white" data-testid="region-admin-welcome-got-it">Got it</button>
    </section>
  );
}
